import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, ChevronRight, Gamepad2, Volume2, VolumeX, X } from "lucide-react";
import {
  CELEBRATION_GAMES, celebrationOrderItems, resolveCelebrationItem,
} from "./rechargeCelebrationConfig";

const ACCENT_CLASSES = {
  amber: "border-amber-400/50 bg-amber-500/10 text-amber-200 shadow-[0_0_24px_rgba(251,191,36,0.22)]",
  sky: "border-sky-400/50 bg-sky-500/10 text-sky-200 shadow-[0_0_24px_rgba(56,189,248,0.22)]",
  red: "border-red-400/50 bg-red-500/10 text-red-200 shadow-[0_0_24px_rgba(248,113,113,0.22)]",
  lime: "border-lime-400/50 bg-lime-500/10 text-lime-200 shadow-[0_0_24px_rgba(163,230,53,0.22)]",
  pink: "border-pink-400/50 bg-pink-500/10 text-pink-200 shadow-[0_0_24px_rgba(244,114,182,0.22)]",
  emerald: "border-emerald-400/50 bg-emerald-500/10 text-emerald-200 shadow-[0_0_24px_rgba(52,211,153,0.22)]",
};

const playVictoryTone = () => {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return;
  const ctx = new AudioCtx();
  [523.25, 659.25, 783.99].forEach((frequency, index) => {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime + index * 0.1);
    gain.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + index * 0.1 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + index * 0.1 + 0.22);
    oscillator.connect(gain).connect(ctx.destination);
    oscillator.start(ctx.currentTime + index * 0.1);
    oscillator.stop(ctx.currentTime + index * 0.1 + 0.24);
  });
  window.setTimeout(() => ctx.close(), 700);
};

function DeliveredItemCard({ item, reducedMotion, sequenceIndex }) {
  const accent = item.gameConfig?.accent || "sky";
  const delay = Math.min(sequenceIndex * 0.16, 0.64);
  return (
    <motion.article
      data-testid={`celebration-item-${item.id || `${item.gameId}-${item.packageId}-${item.playerId}`}`}
      initial={reducedMotion ? false : { opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.34, delay }}
      className={`relative min-w-0 overflow-hidden rounded-xl border p-3 ${ACCENT_CLASSES[accent]}`}
    >
      <div className="relative flex items-center gap-3">
        <div className="relative flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg border border-white/20 bg-black/35">
          <motion.span
            aria-hidden="true"
            className="absolute inset-0 rounded-lg border border-white/50"
            animate={reducedMotion ? { opacity: 0 } : { scale: [0.86, 1.18, 1], opacity: [0, 0.65, 0] }}
            transition={{ duration: 0.72, delay: delay + 0.44, ease: "easeOut" }}
          />
          {item.image ? (
            <motion.img
              data-testid={`celebration-item-image-${item.id || item.packageId}`}
              src={item.image}
              alt={item.packageName}
              initial={reducedMotion ? false : { y: -72, scale: 1.24, rotate: -9, opacity: 0 }}
              animate={reducedMotion ? { opacity: 1 } : { y: [-72, 0, -9, 0], scale: [1.24, 1, 1.07, 1], rotate: [-9, 0, 3, 0], opacity: 1 }}
              transition={{ duration: 0.82, delay: delay + 0.1, ease: "easeOut" }}
              className="relative z-[1] h-full w-full rounded-lg object-cover"
            />
          ) : <Gamepad2 className="relative z-[1] h-6 w-6 text-white/80" />}
        </div>
        <div className="min-w-0 flex-1">
          <p data-testid={`celebration-item-game-${item.id || item.packageId}`} className="text-[10px] font-mono font-bold uppercase tracking-widest text-white/70">{item.gameName}</p>
          <h3 data-testid={`celebration-item-package-${item.id || item.packageId}`} className="mt-0.5 truncate text-sm font-heading font-extrabold text-white">{item.packageName}</h3>
          <motion.p
            data-testid={`celebration-item-player-${item.id || item.packageId}`}
            initial={reducedMotion ? false : { opacity: 0.4 }}
            animate={reducedMotion ? { opacity: 1 } : { opacity: [0.4, 1, 1], color: ["rgba(255,255,255,0.55)", "rgba(255,255,255,1)", "rgba(255,255,255,0.65)"] }}
            transition={{ duration: 0.5, delay: delay + 0.62 }}
            className="mt-1 text-[11px] font-mono text-white/65"
          >Cuenta destino: {item.playerId}</motion.p>
        </div>
      </div>
    </motion.article>
  );
}

export function RechargeSuccessCelebration({ orders, games, socialNetworks = [], onClose, onViewOrders, onBackToStore }) {
  const reducedMotion = useReducedMotion();
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const rawItems = useMemo(() => (orders || []).flatMap(celebrationOrderItems), [orders]);
  const dismiss = useCallback(() => {
    setDismissed(true);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    const onKeyDown = (event) => { if (event.key === "Escape") dismiss(); };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [dismiss]);

  const items = useMemo(
    () => rawItems.map((item) => resolveCelebrationItem(item, games)),
    [rawItems, games],
  );
  const groups = useMemo(() => {
    const grouped = new Map();
    items.forEach((item) => {
      const key = `${item.gameId}::${item.playerId}`;
      grouped.set(key, [...(grouped.get(key) || []), item]);
    });
    return [...grouped.values()];
  }, [items]);

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    if (next) playVictoryTone();
  };

  if (dismissed) return null;

  return (
    <AnimatePresence>
      <motion.div data-testid="celebration-modal" role="dialog" aria-modal="true" aria-labelledby="celebration-title"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] flex items-center justify-center overflow-y-auto bg-black/85 p-3 backdrop-blur-2xl sm:p-6">
        <motion.section
          initial={reducedMotion ? false : { scale: 0.92, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
          className="relative my-auto w-full max-w-2xl overflow-hidden rounded-2xl border-2 border-cyan-400/55 bg-[#090a10] p-5 text-white shadow-[0_0_60px_rgba(6,182,212,0.3),0_0_100px_rgba(147,51,234,0.2)] sm:p-8"
        >
          <button type="button" onClick={dismiss} data-testid="celebration-close-button" aria-label="Cerrar celebración"
            className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-lg border border-zinc-700 bg-black/45 text-zinc-200 transition-[background-color,transform] hover:bg-zinc-800 hover:scale-105">
            <X className="h-5 w-5" />
          </button>
          <header className="relative z-10 flex flex-col items-center text-center">
            <motion.div animate={reducedMotion ? {} : { scale: [1, 1.06, 1] }} transition={{ duration: 2, repeat: Infinity }}
              className="flex h-16 w-16 items-center justify-center rounded-2xl border border-cyan-300/70 bg-cyan-400/15 shadow-[0_0_28px_rgba(34,211,238,0.45)]">
              <CheckCircle2 className="h-9 w-9 text-cyan-200" />
            </motion.div>
            <p data-testid="celebration-kicker" className="mt-4 text-[10px] font-mono font-bold uppercase tracking-[0.18em] text-cyan-300">Entrega confirmada</p>
            <h2 id="celebration-title" data-testid="celebration-title" className="mt-1 text-3xl font-heading font-black uppercase text-white sm:text-4xl">¡Recarga completada!</h2>
            <p data-testid="celebration-summary" className="mt-2 max-w-lg text-xs leading-relaxed text-zinc-300 sm:text-sm">Tus productos fueron entregados. Revisa cada recarga y su jugador a continuación.</p>
          </header>

          <div data-testid="celebration-groups" className="relative z-10 mt-6 max-h-[48vh] space-y-4 overflow-y-auto pr-1">
            {groups.map((group) => {
              const game = group[0]?.gameConfig || CELEBRATION_GAMES[group[0]?.gameKey];
              return (
                <section key={`${group[0]?.gameId}-${group[0]?.playerId}`} data-testid={`celebration-group-${group[0]?.gameId}-${group[0]?.playerId}`}>
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-base" aria-hidden="true">{game?.symbol || "✦"}</span>
                    <h3 data-testid={`celebration-group-title-${group[0]?.gameId}-${group[0]?.playerId}`} className="text-xs font-heading font-bold uppercase tracking-wide text-white">{group[0]?.gameName}</h3>
                    <span className="truncate text-[10px] font-mono text-zinc-500">· ID {group[0]?.playerId}</span>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{group.map((item, itemIndex) => <DeliveredItemCard key={item.id || `${item.packageId}-${item.playerId}`} item={item} reducedMotion={reducedMotion} sequenceIndex={(groups.indexOf(group) * 2) + itemIndex} />)}</div>
                </section>
              );
            })}
          </div>

          {socialNetworks.length > 0 && (
            <section data-testid="celebration-social-cta" className="relative z-10 mt-5 border-t border-zinc-800 pt-4 text-center">
              <p className="text-[10px] font-mono font-bold uppercase tracking-[0.14em] text-cyan-200">Sigue a AG7 para enterarte de nuevas recargas</p>
              <div className="mt-3 flex flex-wrap justify-center gap-2">
                {socialNetworks.map((network) => (
                  <a key={network.id} href={network.url} target="_blank" rel="noopener noreferrer"
                    data-testid={`celebration-social-link-${network.id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-400/35 bg-cyan-500/10 px-3 py-2 text-xs font-bold text-cyan-100 transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-cyan-500/20">
                    {network.icon ? <span className="text-sm leading-none">{network.icon}</span> : <ArrowUpRight className="h-3.5 w-3.5" />}
                    Seguir {network.name}
                  </a>
                ))}
              </div>
            </section>
          )}

          <footer className="relative z-10 mt-6 flex flex-wrap items-center justify-center gap-2 border-t border-zinc-800 pt-4 sm:gap-3">
            <button type="button" onClick={toggleSound} data-testid="celebration-sound-toggle" aria-pressed={soundEnabled}
              className="inline-flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2.5 text-xs font-bold text-zinc-200 transition-colors hover:bg-zinc-800">
              {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />} {soundEnabled ? "Sonido activo" : "Activar sonido"}
            </button>
            <button type="button" onClick={onBackToStore} data-testid="celebration-store-button"
              className="inline-flex items-center gap-2 rounded-lg border border-cyan-500/55 bg-cyan-500/10 px-3 py-2.5 text-xs font-black text-cyan-100 transition-colors hover:bg-cyan-500/20">
              <Gamepad2 className="h-4 w-4" /> Volver a la tienda
            </button>
            <button type="button" onClick={onViewOrders} data-testid="celebration-orders-button"
              className="inline-flex items-center gap-2 rounded-lg bg-amber-400 px-3 py-2.5 text-xs font-black text-[#171207] transition-[background-color,transform] hover:bg-amber-300 hover:-translate-y-0.5">
              Ver pedidos <ChevronRight className="h-4 w-4" />
            </button>
          </footer>
        </motion.section>
      </motion.div>
    </AnimatePresence>
  );
}